/**
 * What to SHOW for the counterparty in a negotiation.
 *
 * ONE function, used by every surface that names a party in a negotiation —
 * the creator's ProposalCard, the worker's NegotiationPanel, and the
 * accepted-worker line in NegotiationSummary. The recurring defect here was
 * the same rule reimplemented per call site and then drifting apart.
 *
 * ── THE BUG THIS CLOSES ────────────────────────────────────────────────────
 * `SupabaseNegotiationService` embedded `profiles:worker_id(display_name)`
 * against the profiles BASE TABLE, whose SELECT policy is owner-or-admin
 * (20260413000003_restrict_profile_pii_to_admin.sql). PostgREST does not error
 * on that — it returns HTTP 200 with the embed set to `null` — so the call
 * site's `profile?.name ?? t.workerId` fallback fired and the creator's UI
 * rendered the worker's raw Privy DID. It looked correct in testing only
 * because admins CAN read profiles, so admin eyes never saw the fallback.
 *
 * Verified against the local stack on 2026-09-15 with a non-admin creator JWT:
 *   profiles:worker_id(...)                          -> {"profiles": null}
 *   public_profiles!proposals_worker_id_fkey(...)    -> {"display_name": ...}
 *
 * This function takes NO id parameter, which is the point: it is structurally
 * incapable of returning a DID, so the leak cannot be reintroduced by an
 * inattentive `??` at a future call site.
 *
 * Returns `null` rather than a placeholder word so the caller supplies a
 * TRANSLATED generic ("Worker" / "Creator"); `lib/` has no `t`. This mirrors
 * candidateDisplayIdentity, which documents the same choice.
 */
export function negotiationPartyName(
  profile?: { displayName?: string | null; handle?: string | null } | null,
): string | null {
  // `.trim() ||` and not `??`: display_name is NOT NULL on profiles and an
  // empty string is real in that column (20260208000003), so `??` would pass
  // "" straight through and render a nameless row.
  const name = (profile?.displayName ?? "").trim();
  if (name) return name;

  const handle = (profile?.handle ?? "").trim();
  if (handle) return handle.startsWith("@") ? handle : `@${handle}`;

  return null;
}
